import React from 'react';
import { NextPage } from 'next';
import { reduxWrapper } from 'store';
import {
  getProviderKinds,
  getStatesForKind,
} from 'lib/DocumentsClients/providers';
import type { GetStatesForKindResponse } from 'types/api/documents/providers';
import { OkResult, ResultPropType, toResultProp } from 'utils/results';
import ProvidersFilterPageView from 'views/ProvidersPage/filter';
import type { ApplicationContext, ProvidersRouteParams } from 'types';
import { buildRouteParams, fullStateName } from 'pages/providers/utils';
import { captureResult } from 'utils/sentry';
import { REVALIDATE_TIME } from 'constants/Revalidate';
import { initializeApplication } from 'state/actions/applicationActions';

interface Props {
  routeParams: ProvidersRouteParams;
  result: OkResult<GetStatesForKindResponse>;
}

// fetch all possible provider kinds to pre-build paths
export async function getStaticPaths() {
  const res = await getProviderKinds();
  const formattedRes = toResultProp(res);

  if (formattedRes.type === ResultPropType.ERR) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const paths = formattedRes.value.items.map((providerKind) => ({
    params: { kind: providerKind },
  }));

  return { paths, fallback: 'blocking' };
}

const StatesListPage: NextPage<Props> = ({ routeParams, result }: Props) => {
  return (
    <ProvidersFilterPageView
      routeParams={routeParams}
      items={result.value.items}
      itemDisplay={fullStateName}
    />
  );
};

export const getStaticProps = reduxWrapper.getStaticProps(
  async ({ store, params }) => {
    const rp = buildRouteParams(params);
    const result = await getStatesForKind(rp.kind || 'XX');
    captureResult(result, { tag: 'enteredURL', value: `/${rp.kind}` });

    const formattedResult = toResultProp(result);

    if (formattedResult.type === ResultPropType.ERR) {
      return {
        notFound: true,
        revalidate: REVALIDATE_TIME,
      };
    }

    // _app.tsx#getInitialProps doesn't fire when building static pages. We have to initialize
    // the app here. TODO: think of a DRY'er way to do this, so we don't forget to when we build
    // more pages statically.
    await store.dispatch(
      initializeApplication({
        query: {},
        req: {},
      } as ApplicationContext) as any,
    );

    return {
      props: {
        routeParams: rp,
        result: formattedResult,
      },
      revalidate: REVALIDATE_TIME,
    };
  },
);

export default StatesListPage;
